import { gql } from '@apollo/client';

const commentsFragment = gql`
  fragment CommentsFields on Comment {
    id
    content
    draftRaw
    createdDateTime
    canEdit
    isGuest
    authorName
    employer {
      id
      username
      name
      initials
      email
      photoThumbnail
    }
    application {
      id
    }
    projectId
    projectTitle
  }
`;

const tagFragment = gql`
  fragment TagFields on Tag {
    id
    name
    special_type
  }
`;

const eventsFragment = gql`
  fragment EventsFields on ApplicationEvent {
    id
    eventType
    dateTime
    eventData
    employer {
      id
      username
      initials
      name
      email
      photoThumbnail
    }
  }
`;

const applicationBasicFragment = gql`
  fragment BasicFields on Application {
    name
    photo
    rating
    selectionLevel
    selectionLevelValue
    rejected
    preselectionPassed
    preselectionPointCount
    phone
    email
    employerView
    consentExpiring
    consentExpiringDate
    consent
    hidden
  }
`;

const ratingsByEmployerFragment = gql`
  fragment RatingsByEmployerFields on Application {
    ratingsByEmployer {
      id
      score
      employer {
        id
        name
        email
        initials
        photoThumbnail
      }
    }
  }
`;

const payPerPerformanceFragment = gql`
  fragment PayPerPerformanceFields on ApplicationPayPerPerformance {
    id
    locked
    cvSummary
    anonymizationStatus
  }
`;

const queryFetchApplication = gql`
  query fetchApplication($applicationId: Int!, $employerViewedUpdate: Boolean, $projectId: Int) {
    application(
      id: $applicationId
      employerViewedUpdate: $employerViewedUpdate
      projectId: $projectId
    ) {
      id
      candidateProfileId
      anonymizedApplicationState
      jobId
      preselectionJobId
      lockedCandidate {
        cityName
        standardizedPositionName
        workExperienceCompanyName
        workExperienceFromDate
        workExperienceToDate
        candidateProfileId
      }
      calendarSlots {
        id
        title
        status
        cancelled
        datetimeFrom
        datetimeTo
        meetingType
        link
        address
        phoneNumber
        cityName
        calendarEmployers {
          id
          organizer
          employer {
            id
            name
          }
        }
        calendarCandidates {
          application {
            id
          }
          status
        }
      }
      interviewAttendances {
        id
        attended
        slotId
      }
      email
      phone
      coverLetter
      fromSite
      canDelete
      comment
      order
      applicationType
      shouldAskForApplicationFeedback
      ...BasicFields
      hiddenAdditionalData {
        id
        hiddenUntil
      }
      companyHide {
        id
        hiddenUntil
        duration
      }
      ...RatingsByEmployerFields
      comments(includePreviousComments: true, option: "project") {
        ...CommentsFields
      }
      applications(option: "project") {
        id
        applicationTime
        projects {
          id
          name
        }
        interviewAttendances {
          id
          attended
        }
        rating
        ratingsByEmployer {
          id
          score
          employer {
            id
            name
            email
            initials
            photoThumbnail
          }
        }
        currentSelectionLevel {
          id
          level
          name
        }
        highestSelectionLevel {
          id
          level
          name
        }
        latestRepliesToCandidate {
          id
          eventType
          dateTime
        }
      }
      attachments {
        id
        type
        ext
      }
      job {
        id
        title
        status
      }
      applicationCVSnapshot {
        id
      }
      events(projectId: $projectId) {
        ...EventsFields
      }
      tags {
        ...TagFields
      }
      preselectionApplication {
        id
        preselectionQuestion {
          id
          required
          question
          passingPoints
          genericQuestion
          genericQuestionType
          preselectionQuestionAnswers {
            id
            answer
            order
            points
          }
        }
        candidateAnswer {
          id
          answerId
          comment
        }
      }
      isWithDynamicProfile
      summary
      relevance
      processingStatus
      feedback
    }
  }
  ${commentsFragment}
  ${applicationBasicFragment}
  ${eventsFragment}
  ${tagFragment}
  ${ratingsByEmployerFragment}
`;

const queryFetchApplications = gql`
  query fetchApplications($id: Int) {
    job(id: $id) {
      applications {
        id
        name
        email
        rating
        selectionLevel
      }
    }
  }
`;

const queryFetchApplicationPayPerPerformance = gql`
  query fetchApplicationPayPerPerformance($applicationId: Int!) {
    application(id: $applicationId) {
      id
      payPerPerformance {
        ...PayPerPerformanceFields
      }
    }
  }
  ${payPerPerformanceFragment}
`;

export {
  applicationBasicFragment,
  commentsFragment,
  payPerPerformanceFragment,
  queryFetchApplication,
  queryFetchApplicationPayPerPerformance,
  queryFetchApplications,
  ratingsByEmployerFragment,
  tagFragment
};

